import { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import apiClient from "utils/apiClient";
import { OVIZIA_SERVICE_BASE_URL } from "constants";

const BASE_URL = `${OVIZIA_SERVICE_BASE_URL}/api/v1/admin/domains`;
const DEFAULT_PAGE = 0;
const DEFAULT_SIZE = 20;

export default function useDomains() {
  const authToken = sessionStorage.getItem("authToken");

  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  // error handler
  const handleAxiosErrors = (err, action = "operation") => {
    let message = "An unexpected error occurred.";

    if (err.response) {
      const status = err.response.status;

      if (status >= 500) {
        message = "Server error. Please try again later.";
      } else {
        message =
          err.response.data?.message ||
          `Failed to ${action}: ${err.response.statusText}`;
      }
    } else if (err.request) {
      message = "No response from server. Check your connection.";
    } else {
      message = err.message || message;
    }

    toast.error(message);
    console.error(`${action} error:`, err);
    setError(message);
  };

  // parse the embedded JSON error string safely
  const parseDomainError = (raw) => {
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return { code: null, message: raw };
    }
  };

  // normalize data
  const formatDomain = (data) => ({
    id: data.id,
    userId: data.userId,
    workspaceId: data.workspaceId,
    websiteId: data.websiteId,
    websiteName: data.websiteName,
    websiteSlug: data.websiteSlug,
    userDomainId: data.userDomainId,
    domainOrderId: data.domainOrderId,
    domainName: data.domainName,
    targetHost: data.targetHost,
    targetIpAddress: data.targetIpAddress,
    primaryDomain: data.primaryDomain,
    status: data.status,
    dnsStatus: data.dnsStatus,
    dnsRecords: data.dnsRecords || [],
    parsedError: parseDomainError(data.errorMessage),
    rawErrorMessage: data.errorMessage,
    hostingStatus: data.hostingStatus,
    hostingErrorMessage: data.hostingErrorMessage,
    hostingBucketName: data.hostingBucketName,
    hostingIpAddress: data.hostingIpAddress,
    hostingBackendBucket: data.hostingBackendBucket,
    hostingUrlMap: data.hostingUrlMap,
    hostingHttpProxy: data.hostingHttpProxy,
    hostingHttpsProxy: data.hostingHttpsProxy,
    hostingSslCertificate: data.hostingSslCertificate,
    hostingHttpForwardingRule: data.hostingHttpForwardingRule,
    hostingHttpsForwardingRule: data.hostingHttpsForwardingRule,
    sslCertificateStatus: data.sslCertificateStatus,
    userDomainStatus: data.userDomainStatus,
    domainRegisteredAt: data.domainRegisteredAt,
    domainExpiresAt: data.domainExpiresAt,
    domainAutoRenew: data.domainAutoRenew,
    domainOrderAmount: data.domainOrderAmount,
    providerBaseAmount: data.providerBaseAmount,
    privacyAmount: data.privacyAmount,
    markupAmount: data.markupAmount,
    markupPercent: data.markupPercent,
    domainOrderCurrency: data.domainOrderCurrency,
    domainOrderStatus: data.domainOrderStatus,
    paymentChargeId: data.paymentChargeId,
    paymentProcessorId: data.paymentProcessorId,
    providerOrderId: data.providerOrderId,
    providerDomainId: data.providerDomainId,
    paymentResponse: data.paymentResponse,
    providerResponse: data.providerResponse,
    transactionSnapshot: data.transactionSnapshot,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  });

  const handlePaginatedResponse = (res, page = 0) => {
    const { content = [], totalPages = 1, totalElements = 0 } = res.data;

    setDomains((prev) =>
      page === 0
        ? content.map(formatDomain)
        : [...prev, ...content.map(formatDomain)],
    );
    setTotalPages(totalPages);
    setTotalElements(totalElements);
  };

  // get all
  const fetchAll = useCallback(
    async (page = DEFAULT_PAGE, size = DEFAULT_SIZE) => {
      if (!authToken) return;

      setLoading(true);
      setError(null);

      try {
        const params = { page, size };
        const res = await apiClient.get(BASE_URL, { params });
        handlePaginatedResponse(res, page);
      } catch (err) {
        handleAxiosErrors(err, "fetch domains");
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    },
    [authToken],
  );

  // search / filter
  const searchDomains = useCallback(
    async ({
      domainName,
      dnsStatus,
      hostingStatus,
      workspaceId,
      primaryDomain,
      page = DEFAULT_PAGE,
      size = DEFAULT_SIZE,
    } = {}) => {
      if (!authToken) return;

      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          page: String(page),
          size: String(size),
        });

        if (domainName) params.append("domainName", domainName);
        if (dnsStatus) params.append("dnsStatus", dnsStatus);
        if (hostingStatus) params.append("hostingStatus", hostingStatus);
        if (workspaceId) params.append("workspaceId", workspaceId);
        if (primaryDomain !== undefined && primaryDomain !== null)
          params.append("primaryDomain", String(primaryDomain));

        const res = await apiClient.get(BASE_URL, { params });

        handlePaginatedResponse(res, page);
      } catch (err) {
        handleAxiosErrors(err, "search domains");
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  // get by id (useful for deep-linking straight to a domain's detail)
  const getDomainById = useCallback(
    async (id) => {
      if (!authToken || !id) return;

      try {
        const res = await apiClient.get(`${BASE_URL}/${id}`);
        return formatDomain(res.data);
      } catch (err) {
        handleAxiosErrors(err, "fetch domain by id");
      }
    },
    [authToken],
  );

  useEffect(() => {
    if (authToken) fetchAll();
  }, [authToken, fetchAll]);

  return {
    domains,
    loading,
    error,
    initialLoad,
    totalPages,
    totalElements,
    fetchAll,
    searchDomains,
    getDomainById,
  };
}
